
import {
  string, array, number, mixed, addMethod, setLocale, ValidationError, date, object,
  type AnyObject, type Flags, type ISchema, type Maybe, type ObjectSchema, type Reference
} from 'yup';

import type { RecursiveKeys } from '../utils/form';

/**
 * Marker type satisfied by every value yup accepts inside an object shape — any schema or a `ref()`.
 */
type BaseSchema =
  | {
      __isYupSchema__: boolean;
    }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | ISchema<any, any, any, any>
  | Reference<unknown>;

/**
 * The shape argument accepted by `typedShape`.
 *
 * For each key of `T`:
 * - array fields accept any yup schema
 * - plain-object fields accept an object schema whose keys belong to that nested type
 * - primitives accept any yup schema
 *
 * Keys not present on `T` are rejected by TypeScript, giving autocomplete and compile-time errors.
 * Only key names are checked — yup's inferred value types are intentionally left unconstrained.
 */
export type FormShape<T> = {
  [K in keyof T]?: T[K] extends unknown[]
    ? BaseSchema
    : // eslint-disable-next-line @typescript-eslint/no-explicit-any
      T[K] extends Record<string, any>
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ObjectSchema<Partial<Record<keyof T[K], any>>, any, any, any>
      : BaseSchema;
};

/**
 * Returns the given field path unchanged, but only accepts paths that exist on `T`.
 *
 * Handed to the `typedShape` builder callback so sibling references inside `when()` are checked
 * at compile time. `when()` itself is declared by yup as `when(keys: string | string[], ...)`, so
 * narrowing it directly is not possible — the key has to be validated where it is written.
 */
export type TypedShapeKey<T> = <K extends RecursiveKeys<T>>(key: K) => K;

/**
 * Prevents TypeScript from inferring a generic parameter from the annotated position, so the type
 * is resolved purely from the contextual (expected) type at the usage site.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type NoInferType<T> = [T][T extends any ? 0 : never];

declare module 'yup' {
  interface ArraySchema<TIn extends any[] | null | undefined, TContext> { // eslint-disable-line @typescript-eslint/no-unused-vars
    uniqueItems<K>(
      arrayFieldName: string,
      transformItem: (item: K) => string | number
    ): this;
  }

  interface DateSchema {
    notInThePast(): this;
  }
  interface NumberSchema {
    maxDecimalPlaces(maxDecimalPlaces: number): this;
  }

  interface ObjectSchema<
    TIn extends Maybe<AnyObject>, // eslint-disable-line @typescript-eslint/no-unused-vars
    TContext = AnyObject, // eslint-disable-line @typescript-eslint/no-unused-vars
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
    TDefault = any,
    TFlags extends Flags = '' // eslint-disable-line @typescript-eslint/no-unused-vars
  > {
    /**
     * `shape()` with the keys type-checked against `T`, removing the need for
     * `as ObjectSchema<Partial<T>>` casts at call sites.
     *
     * Nested object fields resolve `T` from the surrounding shape, so they get autocomplete and
     * compile-time key checks without repeating the type argument.
     *
     * The signature deliberately does not reference the surrounding type parameters — doing so
     * makes TypeScript treat every `ObjectSchema` instantiation as invariant and breaks
     * assignability across the codebase.
     *
     * @example
     * export const validationSchema = object().typedShape<FormValues>({
     *   title: string().required(),
     *   field: object().typedShape({ nested_field: string().required() }), // ✅ keys checked
     *   field: object().typedShape({ wrong_key: string() }),               // ❌ 'wrong_key' does not exist
     *   wrong_field: string(),                                             // ❌ not a key of FormValues
     * });
     */
    typedShape<T>(
      shape: NoInferType<FormShape<T>>,
      excludes?: [string, string][]
    ): ObjectSchema<Partial<T>>;
    /**
     * Builder form of `typedShape`. The callback receives `n`, which type-checks sibling field
     * paths against `T` — use it for the `keys` argument of `when()`.
     *
     * @example
     * export const validationSchema = object().typedShape<FormValues>((n) => ({
     *   body: string().when(n('enabled'), { is: true, then: (schema) => schema.required() }), // ✅
     *   title: string().when(n('wrong_key'), { is: true }),                                   // ❌ not a key of FormValues
     * }));
     */
    typedShape<T>(
      buildShape: (n: TypedShapeKey<T>) => NoInferType<FormShape<T>>,
      excludes?: [string, string][]
    ): ObjectSchema<Partial<T>>;
  }
}

setLocale({
  mixed: {
    default: 'validation_invalid',
    required: 'validation_required',
  },
  string: {
    email: 'validation_string_email',
    min: ({ min }) => ({ key: 'validation_string_min', values: { min } }),
    max: ({ max }) => ({ key: 'validation_string_max', values: { max } }),
    length: ({ length }) => ({ key: 'validation_string_length', values: { length } }),
    url: 'validation_url'
  },
  number: {
    integer: 'validation_integer',
    positive: 'validation_number_positive',
    negative: 'validation_number_negative',
    min: ({ min }) => ({ key: 'validation_number_min', values: { min } }),
    max: ({ max }) => ({ key: 'validation_number_max', values: { max } }),
    moreThan: ({ more }) => ({ key: 'validation_string_min', values: { min: more } }),
    lessThan: ({ less }) => ({ key: 'validation_string_max', values: { max: less } }),
  },
  array: {
    max: ({ max }) => ({ key: 'validation_array_max', values: { count: max } }),
    min: ({ min }) => ({ key: 'validation_array_min', values: { count: min } }),
  }
});

// validates if the coordinates are valid
const coordsSchema = array().of(
  array().of(number()).min(2).max(2)
).min(2);
addMethod(string, 'coordinates', function () {
  return this.test('coordinates', 'validation_coordinates', function (value) {
    try {
      const coords = JSON.parse(value as any);
      coordsSchema.validateSync(coords);
      return true;
    } catch (err) {
      return false;
    }
  });
});

addMethod(date, 'notInThePast', function () {
  return this.test('notInThePast', 'validation_dateNotInThePast', function (value) {
    if (!value) return true; // allow empty value

    return new Date(value) >= new Date();
  });
});

// field is required if another field is true/false
addMethod(mixed, 'requiredIf', function (fieldToLookAt, boolValue = true) {
  return this.when(fieldToLookAt, { is: boolValue, then: field => field.required() });
});

// min validator, but with allow empty string
addMethod(string, 'minAndEmpty', function (min = 0) {
  return this.test('minAndEmpty',
    { key: 'validation_string_min', values: { min } },
    val => {
      if (!val) return true;
      return val.length >= min;
    });
});

/**
 * Transforms empty string to null for number fields - USE ONLY FOR V1 DESIGN SYSTEM NUMBER INPUTS
 */
export const transformStringToNum = (value: any, originalValue: any) => {
  return originalValue === '' ? null : value;
};

addMethod(string, 'email', function () {
  return this.trim().test('email', 'emailIsInvalid', function (value) {
    if (!value) return true;
    const regex = '^[a-zA-Z0-9.!\\#$%&\'*+\\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$';
    return new RegExp(regex).test(value)
      //eslint-disable-next-line
      && /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(value);
  });
});

addMethod(array, 'uniqueItems', function (arrayFieldName: string, transformItem: <T, D>(item: T) => D) {
  return this.test('uniqueItems', '', function (arrayValues) {
    const errors: ValidationError[] = [];
    arrayValues?.forEach((item, index) => {
      const propertyValue = item[arrayFieldName];

      if (propertyValue && arrayValues?.filter(compareItem => {
        return transformItem(item) === transformItem(compareItem);
      }).length > 1) {
        errors.push(
          this.createError({
            path: `${this.path}[${index}].${arrayFieldName}`,
            message: 'validation_uniqueness',
          })
        );
      }
    });

    if (errors.length > 0) {
      throw new ValidationError(errors);
    }

    return true;
  });
});

// validates if number value has not more than X decimal places
addMethod(number, 'maxDecimalPlaces', function (maxDecimalPlaces: number) {
  return this.test('test-number-of-decimal-places', 'validation_numberDecimalPlaces', function (value) {
    if (value === undefined || value === null) return true;
    const decimalPart = value.toString().split('.')[1];
    return !decimalPart || decimalPart.length <= maxDecimalPlaces;
  });
});

// shape(), but with the keys type-checked against the form values type
addMethod(object, 'typedShape', function (shape, excludes) {
  // the builder form receives an identity helper that only exists to type-check field paths
  const resolvedShape = typeof shape === 'function' ? shape((key: string) => key) : shape;

  return this.shape(resolvedShape, excludes);
});